// Importa el Excel de un AÑO ANTERIOR (histórico) para comparativas, aislado
// del dato vivo de 2026. Dos modos: TODAS las hojas de una vez (como el
// importador del año en curso) o una sola hoja con mapeo manual (avanzado).
import React, { useState } from "react";
import axios from "axios";
import { X, Upload, Loader2, FileSpreadsheet, CheckCircle2, Layers, Settings2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";

export default function HistoricoImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [year, setYear] = useState("");
  const [mode, setMode] = useState("all"); // 'all' = todas las hojas, 'one' = una hoja
  const [sheetName, setSheetName] = useState("");
  const [mapping, setMapping] = useState({});
  const [allResult, setAllResult] = useState(null);

  const sheet = preview?.sheets?.find((s) => s.name === sheetName);

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    setAllResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/historico/preview`, fd);
      setPreview(data);
      setYear(String(data.guessed_year));
      const first = data.sheets?.[0];
      if (first) { setSheetName(first.name); setMapping(first.mapping || {}); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo analizar el Excel");
    } finally { setAnalyzing(false); }
  };

  const onSheetChange = (name) => {
    setSheetName(name);
    const s = preview?.sheets?.find((x) => x.name === name);
    setMapping(s?.mapping || {});
  };

  const setField = (field, header) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!header) delete next[field];
      else next[field] = header;
      return next;
    });
  };

  const importAll = async () => {
    if (!file || !year) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ejercicio", String(parseInt(year)));
      fd.append("replace", "true");
      const { data } = await axios.post(`${API}/historico/confirm-all`, fd);
      setAllResult(data);
      toast.success(`Ejercicio ${data.ejercicio}: ${data.inserted} registros de ${data.hojas_importadas} hoja(s)`);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar");
    } finally { setImporting(false); }
  };

  const importOne = async () => {
    if (!file || !sheetName || !year) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ejercicio", String(parseInt(year)));
      fd.append("sheet_name", sheetName);
      fd.append("mapping_json", JSON.stringify(mapping));
      fd.append("replace", "true");
      const { data } = await axios.post(`${API}/historico/confirm`, fd);
      toast.success(`Importado ejercicio ${data.ejercicio}: ${data.inserted} registros (hoja ${data.sheet})`);
      onImported?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar");
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="historico-import-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white rounded-t-xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
              <FileSpreadsheet size={20} className="text-indigo-700" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Importar año anterior (histórico)</h3>
              <p className="text-xs text-gray-500 mt-0.5">Se guarda aparte, etiquetado por ejercicio; no entra en el pipeline ni en los KPIs del año en curso.</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="historico-close-btn" className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Paso 1 · fichero */}
          <div className="flex items-center gap-2">
            <input type="file" accept=".xlsx,.xlsm" data-testid="historico-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setAllResult(null); }}
              className="text-xs flex-1 border border-gray-200 rounded-lg px-2 py-1.5" />
            <button onClick={analyze} disabled={!file || analyzing} data-testid="historico-analyze-btn"
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-gray-900 text-white rounded-lg px-3 py-2 hover:bg-gray-700 disabled:opacity-40">
              {analyzing ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} Analizar
            </button>
          </div>
          {!preview && (
            <p className="text-[11px] text-gray-400 flex items-center gap-1 -mt-1" data-testid="historico-all-hint">
              <Layers size={12} /> Por defecto se importan <b className="text-gray-500">todas las hojas</b> del Excel de una vez; tras analizar podrás cambiar a una sola hoja.
            </p>
          )}

          {preview && (
            <>
              {/* Paso 2 · año + modo */}
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-600">
                  Ejercicio / Año
                  <input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="historico-year-input"
                    className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-semibold" />
                </label>
                <div className="text-xs text-gray-600">
                  Qué importar
                  <div className="mt-1 grid grid-cols-2 gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button onClick={() => setMode("all")} data-testid="historico-mode-all"
                      className={`flex items-center justify-center gap-1 text-[11px] font-semibold rounded-md px-2 py-1.5 transition ${mode === "all" ? "bg-white shadow text-indigo-700" : "text-gray-500 hover:text-gray-700"}`}>
                      <Layers size={12} /> Todas
                    </button>
                    <button onClick={() => setMode("one")} data-testid="historico-mode-one"
                      className={`flex items-center justify-center gap-1 text-[11px] font-semibold rounded-md px-2 py-1.5 transition ${mode === "one" ? "bg-white shadow text-indigo-700" : "text-gray-500 hover:text-gray-700"}`}>
                      <Settings2 size={12} /> Una hoja
                    </button>
                  </div>
                </div>
              </div>

              {/* ---------- MODO: TODAS LAS HOJAS ---------- */}
              {mode === "all" && (
                <div className="space-y-3">
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3" data-testid="historico-all-summary">
                    <div className="text-[11px] text-indigo-800 flex items-center gap-1.5 font-semibold mb-1.5">
                      <Layers size={13} /> {preview.sheets.length} hoja(s) detectada(s) — se importan todas de una vez
                    </div>
                    <div className="space-y-0.5 max-h-40 overflow-y-auto">
                      {preview.sheets.map((s) => {
                        const ok = (s.coverage || 0) >= 2 && s.n_rows > 0;
                        return (
                          <div key={s.name} className="flex items-center justify-between text-[11px] text-gray-600">
                            <span className="truncate pr-2">{s.name}</span>
                            <span className={`shrink-0 font-medium ${ok ? "text-emerald-700" : "text-gray-400"}`}>
                              {ok ? `${s.n_rows} filas · ${s.coverage} campos` : "se omite (resumen / pocas columnas)"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {allResult ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="historico-all-result">
                      <div className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">
                        <CheckCircle2 size={16} /> {allResult.inserted} registros importados ({allResult.hojas_importadas} hoja/s)
                      </div>
                      <div className="mt-2 space-y-0.5 max-h-36 overflow-y-auto">
                        {allResult.por_hoja.map((h, i) => (
                          <div key={i} className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-700 truncate pr-2">{h.sheet}</span>
                            <span className={h.inserted > 0 ? "text-emerald-700 font-semibold" : "text-gray-400"}>
                              {h.inserted > 0 ? `${h.inserted} filas` : (h.motivo || "0")}
                            </span>
                          </div>
                        ))}
                      </div>
                      <button onClick={onClose} data-testid="historico-all-done-btn"
                        className="mt-3 w-full bg-emerald-600 text-white text-xs font-semibold py-2 rounded-lg hover:bg-emerald-700">Cerrar</button>
                    </div>
                  ) : (
                    <>
                      <button onClick={importAll} disabled={importing || !year} data-testid="historico-import-all-btn"
                        className="w-full flex items-center justify-center gap-2 bg-[#007934] text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-[#006528] disabled:opacity-40">
                        {importing ? <Loader2 className="animate-spin" size={14} /> : <Layers size={14} />}
                        {importing ? "Importando todas las hojas…" : `Importar todas las hojas como ejercicio ${year}`}
                      </button>
                      <p className="text-[10px] text-gray-400 text-center">Reimportar el mismo año reemplaza sus registros (idempotente). Las hojas de resumen sin columnas útiles se omiten solas.</p>
                    </>
                  )}
                </div>
              )}

              {/* ---------- MODO: UNA HOJA (avanzado) ---------- */}
              {mode === "one" && (
                <div className="space-y-4">
                  <label className="text-xs text-gray-600 block">
                    Hoja del Excel
                    <select value={sheetName} onChange={(e) => onSheetChange(e.target.value)} data-testid="historico-sheet-select"
                      className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                      {preview.sheets.map((s) => (
                        <option key={s.name} value={s.name}>{s.name} · {s.n_rows} filas · {s.coverage} campos</option>
                      ))}
                    </select>
                  </label>

                  {sheet && (
                    <div>
                      <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wide mb-1.5">Correspondencia de columnas</div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="historico-mapping">
                        {preview.fields.map((f) => (
                          <div key={f} className="flex items-center gap-1.5">
                            <span className="text-[11px] text-gray-600 w-28 shrink-0 truncate" title={preview.labels[f]}>{preview.labels[f] || f}</span>
                            <select value={mapping[f] || ""} onChange={(e) => setField(f, e.target.value)} data-testid={`historico-map-${f}`}
                              className="flex-1 min-w-0 border border-gray-200 rounded px-1.5 py-1 text-[11px]">
                              <option value="">(ninguno)</option>
                              {sheet.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {sheet?.sample?.length > 0 && (
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-2.5 text-[10px] text-gray-500" data-testid="historico-sample">
                      <b>Vista previa:</b> {sheet.sample.slice(0, 2).map((r, i) => (
                        <div key={i} className="truncate mt-0.5">{[r.expediente, r.provincia, r.direccion_general, r.importe ? `${Math.round(r.importe).toLocaleString("es-ES")} €` : ""].filter(Boolean).join(" · ")}</div>
                      ))}
                    </div>
                  )}

                  <button onClick={importOne} disabled={importing || !year || !sheetName} data-testid="historico-import-btn"
                    className="w-full flex items-center justify-center gap-2 bg-[#007934] text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-[#006528] disabled:opacity-40">
                    {importing ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                    {importing ? "Importando…" : `Importar ${sheet?.n_rows || ""} filas como ejercicio ${year}`}
                  </button>
                  <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1">
                    <AlertTriangle size={11} /> Reimportar el mismo año reemplaza sus registros (idempotente).
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
